import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../components/AuthContext';
const API_BASE = import.meta.env.VITE_API_BASE;
import '../styles/AdminConsole.css';

/* ── Tabs ── */
const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'users',    label: 'Users' },
  { key: 'workspaces', label: 'Workspaces' },
  { key: 'access',   label: 'Access Management' },
  { key: 'approvals', label: 'Approvals' },
];

function AdminConsole() {
  const { apiFetch, user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Access management state
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [wsMembers, setWsMembers] = useState([]);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantQuery, setGrantQuery] = useState('');
  const [grantDropdownOpen, setGrantDropdownOpen] = useState(false);
  const [grantRole, setGrantRole] = useState('editor');
  const [membersLoading, setMembersLoading] = useState(false);
  const grantInputRef = React.useRef(null);

  // Search filters
  const [userSearch, setUserSearch] = useState('');
  const [wsSearch, setWsSearch] = useState('');
  const [pendingUsers, setPendingUsers] = useState([]);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); };

  // Close grant dropdown on click outside
  React.useEffect(() => {
    const handler = (e) => {
      if (grantInputRef.current && !grantInputRef.current.contains(e.target)) {
        setGrantDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ── Data fetching ── */
  const loadStats = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/auth/admin/stats`);
      if (res.ok) setStats(await res.json());
    } catch {}
  }, [apiFetch]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/admin/users`);
      if (res.ok) { const d = await res.json(); setUsers(d.users || []); }
    } catch {}
    setLoading(false);
  }, [apiFetch]);

  const loadWorkspaces = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/admin/workspaces`);
      if (res.ok) { const d = await res.json(); setWorkspaces(d.workspaces || []); }
    } catch {}
    setLoading(false);
  }, [apiFetch]);

  const loadMembers = useCallback(async (wsId) => {
    setMembersLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/auth/admin/workspaces/${wsId}/members`);
      if (res.ok) { const d = await res.json(); setWsMembers(d.members || []); }
    } catch {}
    setMembersLoading(false);
  }, [apiFetch]);

  const loadPendingUsers = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE}/auth/admin/pending-users`);
      if (res.ok) { const d = await res.json(); setPendingUsers(d.pending_users || []); }
    } catch {}
  }, [apiFetch]);

  useEffect(() => {
    loadStats();
    loadUsers();
    loadWorkspaces();
    loadPendingUsers();
  }, [loadStats, loadUsers, loadWorkspaces, loadPendingUsers]);

  useEffect(() => {
    if (selectedWorkspace) loadMembers(selectedWorkspace.id);
  }, [selectedWorkspace, loadMembers]);

  /* ── Actions ── */
  const toggleAdmin = async (userId, currentIsAdmin) => {
    setError('');
    const res = await apiFetch(`${API_BASE}/auth/admin/users/${userId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_admin: !currentIsAdmin }),
    });
    if (res.ok) {
      flash(currentIsAdmin ? 'Admin role removed' : 'Admin role granted');
      loadUsers();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to update user');
    }
  };

  const grantAccess = async (e) => {
    e.preventDefault();
    if (!grantEmail.trim() || !selectedWorkspace) return;
    setError('');
    const res = await apiFetch(`${API_BASE}/auth/admin/workspaces/${selectedWorkspace.id}/members`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: grantEmail.trim(), role: grantRole }),
    });
    if (res.ok) {
      flash(`Access granted to ${grantEmail.trim()}`);
      setGrantEmail('');
      setGrantQuery('');
      loadMembers(selectedWorkspace.id);
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to grant access');
    }
  };

  const removeMember = async (memberId, memberName) => {
    if (!selectedWorkspace) return;
    if (!window.confirm(`Remove ${memberName} from "${selectedWorkspace.name}"?`)) return;
    setError('');
    const res = await apiFetch(`${API_BASE}/auth/admin/workspaces/${selectedWorkspace.id}/members/${memberId}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      flash(`${memberName} removed`);
      loadMembers(selectedWorkspace.id);
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to remove member');
    }
  };

  const approveUser = async (userId) => {
    setError('');
    const res = await apiFetch(`${API_BASE}/auth/admin/users/${userId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'approve' }),
    });
    if (res.ok) {
      flash('User approved — they can now sign in');
      loadPendingUsers(); loadUsers(); loadStats();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to approve user');
    }
  };

  const rejectUser = async (userId, name) => {
    if (!window.confirm(`Reject registration for ${name}? This cannot be undone.`)) return;
    setError('');
    const res = await apiFetch(`${API_BASE}/auth/admin/users/${userId}/review`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'reject' }),
    });
    if (res.ok) {
      flash('Registration rejected');
      loadPendingUsers(); loadUsers(); loadStats();
    } else {
      const err = await res.json().catch(() => ({}));
      setError(err.detail || 'Failed to reject user');
    }
  };

  /* ── Helpers ── */
  const initials = (name) => {
    const parts = (name || '').split(' ').filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return (name || '?')[0].toUpperCase();
  };

  const DOT_COLORS = ['#6366f1','#0f766e','#b45309','#475569','#e11d48','#0284c7','#7c3aed','#059669'];
  const filteredUsers = users.filter(u => {
    const q = userSearch.toLowerCase();
    return (u.display_name || u.name || '').toLowerCase().includes(q) ||
           (u.username || '').toLowerCase().includes(q) ||
           u.email.toLowerCase().includes(q);
  });
  const filteredWorkspaces = workspaces.filter(ws =>
    ws.name.toLowerCase().includes(wsSearch.toLowerCase()) ||
    ws.owner.name.toLowerCase().includes(wsSearch.toLowerCase()) ||
    ws.owner.email.toLowerCase().includes(wsSearch.toLowerCase())
  );

  return (
    <div className="adm-page">
      {/* Hero */}
      <section className="adm-hero">
        <span className="adm-label">ADMINISTRATION</span>
        <h1>Admin Console</h1>
        <p>Manage users, workspaces, access control, and monitor platform usage.</p>
      </section>

      {/* Flash messages */}
      {error && <div className="adm-alert adm-alert-error">{error} <button onClick={() => setError('')}>×</button></div>}
      {success && <div className="adm-alert adm-alert-success">{success}</div>}

      {/* Tabs */}
      <nav className="adm-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            className={`adm-tab ${tab === t.key ? 'active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.label}
            {t.key === 'approvals' && pendingUsers.length > 0 && (
              <span className="adm-tab-badge">{pendingUsers.length}</span>
            )}
          </button>
        ))}
      </nav>

      {/* ═══════════ Overview Tab ═══════════ */}
      {tab === 'overview' && (
        <div className="adm-section">
          <div className="adm-stat-grid">
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-indigo">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="10" cy="6" r="3" /><path d="M3 18c0-4 3.5-7 7-7s7 3 7 7" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.user_count ?? '—'}</span>
                <span className="adm-stat-label">Total Users</span>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-teal">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="2" y="3" width="16" height="14" rx="2" /><line x1="2" y1="8" x2="18" y2="8" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.workspace_count ?? '—'}</span>
                <span className="adm-stat-label">Workspaces</span>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-amber">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M4 4h12v4l-2 2 2 2v4H4v-4l2-2-2-2z" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.total_configs ?? '—'}</span>
                <span className="adm-stat-label">Config Files</span>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-slate">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <rect x="2" y="2" width="16" height="16" rx="2" />
                  <line x1="2" y1="7" x2="18" y2="7" /><line x1="7" y1="7" x2="7" y2="18" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.total_datasets ?? '—'}</span>
                <span className="adm-stat-label">Datasets Generated</span>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-rose">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M9 2h2l1 4h5l-4 3 1.5 5L10 11.5 5.5 14 7 9 3 6h5z" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.total_files ?? '—'}</span>
                <span className="adm-stat-label">Total Files</span>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-emerald">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="10" cy="10" r="7" /><polyline points="10 6 10 10 13 12" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.total_size_mb ?? '—'} MB</span>
                <span className="adm-stat-label">Disk Usage</span>
              </div>
            </div>
            <div className="adm-stat-card">
              <span className="adm-stat-icon icon-amber">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <circle cx="10" cy="10" r="7" /><path d="M10 6v4h3" />
                </svg>
              </span>
              <div className="adm-stat-body">
                <span className="adm-stat-num">{stats?.pending_count ?? '—'}</span>
                <span className="adm-stat-label">Pending Approvals</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ Users Tab ═══════════ */}
      {tab === 'users' && (
        <div className="adm-section">
          <div className="adm-toolbar">
            <input
              type="text"
              className="adm-search"
              placeholder="Search users by name or email…"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            <span className="adm-count">{filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Workspaces</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u, i) => (
                  <tr key={u.id}>
                    <td>
                      <div className="adm-user-cell">
                        <div className="adm-avatar" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                          {initials(u.display_name || u.name)}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                          <span className="adm-name">{u.display_name || u.name}</span>
                          {u.username && <span className="adm-mono" style={{ fontSize: '0.78em', opacity: 0.6 }}>@{u.username}</span>}
                        </div>
                      </div>
                    </td>
                    <td className="adm-mono">{u.email}</td>
                    <td><span className="adm-badge adm-badge-neutral">{u.workspace_count}</span></td>
                    <td>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {u.is_admin
                          ? <span className="adm-badge adm-badge-admin">Admin</span>
                          : <span className="adm-badge adm-badge-user">User</span>}
                        {u.approval_status === 'pending' && <span className="adm-badge adm-badge-pending">Pending</span>}
                        {u.approval_status === 'rejected' && <span className="adm-badge adm-badge-rejected">Rejected</span>}
                      </div>
                    </td>
                    <td className="adm-date">{new Date(u.created_at).toLocaleDateString()}</td>
                    <td>
                      {u.id !== user?.id && (
                        <button
                          className={`adm-action-btn ${u.is_admin ? 'adm-action-revoke' : 'adm-action-grant'}`}
                          onClick={() => toggleAdmin(u.id, u.is_admin)}
                          title={u.is_admin ? 'Revoke admin' : 'Grant admin'}
                        >
                          {u.is_admin ? 'Revoke Admin' : 'Make Admin'}
                        </button>
                      )}
                      {u.id === user?.id && <span className="adm-you-badge">You</span>}
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan={6} className="adm-empty">No users found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════ Workspaces Tab ═══════════ */}
      {tab === 'workspaces' && (
        <div className="adm-section">
          <div className="adm-toolbar">
            <input
              type="text"
              className="adm-search"
              placeholder="Search workspaces…"
              value={wsSearch}
              onChange={(e) => setWsSearch(e.target.value)}
            />
            <span className="adm-count">{filteredWorkspaces.length} workspace{filteredWorkspaces.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="adm-table-wrap">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Owner</th>
                  <th>Members</th>
                  <th>Configs</th>
                  <th>Datasets</th>
                  <th>Files</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWorkspaces.map((ws, i) => (
                  <tr key={ws.id}>
                    <td>
                      <div className="adm-user-cell">
                        <div className="adm-avatar" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                          {ws.name[0].toUpperCase()}
                        </div>
                        <span className="adm-name">{ws.name}</span>
                      </div>
                    </td>
                    <td>
                      <div className="adm-owner-cell">
                        <span>{ws.owner.name}</span>
                        <span className="adm-sub">{ws.owner.email}</span>
                      </div>
                    </td>
                    <td><span className="adm-badge adm-badge-neutral">{ws.member_count}</span></td>
                    <td>{ws.config_count}</td>
                    <td>{ws.output_count}</td>
                    <td>{ws.total_files}</td>
                    <td className="adm-date">{new Date(ws.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        className="adm-action-btn adm-action-manage"
                        onClick={() => { setSelectedWorkspace(ws); setTab('access'); }}
                      >
                        Manage Access
                      </button>
                    </td>
                  </tr>
                ))}
                {filteredWorkspaces.length === 0 && (
                  <tr><td colSpan={8} className="adm-empty">No workspaces found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════ Access Management Tab ═══════════ */}
      {tab === 'access' && (
        <div className="adm-section">
          <div className="adm-access-layout">
            {/* Left: workspace picker */}
            <aside className="adm-ws-picker">
              <h3>Workspaces</h3>
              <input
                type="text"
                className="adm-search adm-search-sm"
                placeholder="Filter…"
                value={wsSearch}
                onChange={(e) => setWsSearch(e.target.value)}
              />
              <div className="adm-ws-list">
                {filteredWorkspaces.map((ws, i) => (
                  <button
                    key={ws.id}
                    className={`adm-ws-item ${selectedWorkspace?.id === ws.id ? 'active' : ''}`}
                    onClick={() => setSelectedWorkspace(ws)}
                  >
                    <div className="adm-avatar adm-avatar-sm" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                      {ws.name[0].toUpperCase()}
                    </div>
                    <div className="adm-ws-item-info">
                      <span className="adm-ws-item-name">{ws.name}</span>
                      <span className="adm-ws-item-owner">{ws.owner.name} · {ws.member_count} member{ws.member_count !== 1 ? 's' : ''}</span>
                    </div>
                  </button>
                ))}
              </div>
            </aside>

            {/* Right: member management */}
            <div className="adm-members-panel">
              {!selectedWorkspace ? (
                <div className="adm-empty-state">
                  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" width="48" height="48">
                    <rect x="1.5" y="1.5" width="17" height="17" rx="2" />
                    <line x1="1.5" y1="7" x2="18.5" y2="7" /><line x1="7" y1="7" x2="7" y2="18.5" />
                  </svg>
                  <p>Select a workspace to manage its members.</p>
                </div>
              ) : (
                <>
                  <div className="adm-members-header">
                    <div>
                      <h3>{selectedWorkspace.name}</h3>
                      <span className="adm-sub">Owned by {selectedWorkspace.owner.name} ({selectedWorkspace.owner.email})</span>
                    </div>
                    <button className="adm-refresh-btn" onClick={() => loadMembers(selectedWorkspace.id)}>
                      ↻ Refresh
                    </button>
                  </div>

                  {/* Grant access form */}
                  <form className="adm-grant-form" onSubmit={grantAccess}>
                    <div className="adm-user-picker" ref={grantInputRef}>
                      <input
                        type="text"
                        className="adm-search"
                        placeholder="Search user by name, email, or username…"
                        value={grantQuery}
                        onChange={(e) => {
                          setGrantQuery(e.target.value);
                          setGrantEmail('');
                          setGrantDropdownOpen(true);
                        }}
                        onFocus={() => setGrantDropdownOpen(true)}
                        autoComplete="off"
                      />
                      {grantDropdownOpen && grantQuery.trim().length > 0 && (() => {
                        const q = grantQuery.toLowerCase();
                        const memberIds = new Set(wsMembers.map(m => m.id));
                        const matches = users.filter(u =>
                          !memberIds.has(u.id) &&
                          ((u.display_name || u.name || '').toLowerCase().includes(q) ||
                           (u.username || '').toLowerCase().includes(q) ||
                           u.email.toLowerCase().includes(q))
                        ).slice(0, 8);
                        if (matches.length === 0) return (
                          <div className="adm-picker-dropdown">
                            <div className="adm-picker-empty">No users found</div>
                          </div>
                        );
                        return (
                          <div className="adm-picker-dropdown">
                            {matches.map((u, i) => (
                              <button
                                key={u.id}
                                type="button"
                                className="adm-picker-option"
                                onClick={() => {
                                  setGrantEmail(u.email);
                                  setGrantQuery(`${u.display_name || u.name} (${u.email})`);
                                  setGrantDropdownOpen(false);
                                }}
                              >
                                <div className="adm-avatar adm-avatar-sm" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                                  {initials(u.display_name || u.name)}
                                </div>
                                <div className="adm-picker-info">
                                  <span className="adm-picker-name">{u.display_name || u.name}{u.username ? ` @${u.username}` : ''}</span>
                                  <span className="adm-picker-email">{u.email}</span>
                                </div>
                              </button>
                            ))}
                          </div>
                        );
                      })()}
                    </div>
                    <select
                      className="adm-role-select"
                      value={grantRole}
                      onChange={(e) => setGrantRole(e.target.value)}
                    >
                      <option value="editor">Editor</option>
                      <option value="viewer">Viewer</option>
                    </select>
                    <button type="submit" className="adm-grant-btn" disabled={!grantEmail}>Grant Access</button>
                  </form>

                  {/* Members list */}
                  <div className="adm-table-wrap">
                    {membersLoading ? (
                      <div className="adm-empty">Loading members…</div>
                    ) : (
                      <table className="adm-table">
                        <thead>
                          <tr>
                            <th>Member</th>
                            <th>Email</th>
                            <th>Role</th>
                            <th>Joined</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {wsMembers.map((m, i) => (
                            <tr key={m.id}>
                              <td>
                                <div className="adm-user-cell">
                                  <div className="adm-avatar adm-avatar-sm" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                                    {initials(m.name)}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                    <span>{m.name}</span>
                                    {m.username && <span className="adm-mono" style={{ fontSize: '0.78em', opacity: 0.6 }}>@{m.username}</span>}
                                  </div>
                                </div>
                              </td>
                              <td className="adm-mono">{m.email}</td>
                              <td>
                                <span className={`adm-badge ${m.role === 'owner' ? 'adm-badge-owner' : m.role === 'editor' ? 'adm-badge-editor' : 'adm-badge-viewer'}`}>
                                  {m.role}
                                </span>
                              </td>
                              <td className="adm-date">{new Date(m.joined_at).toLocaleDateString()}</td>
                              <td>
                                {m.role !== 'owner' ? (
                                  <button
                                    className="adm-action-btn adm-action-revoke"
                                    onClick={() => removeMember(m.id, m.name)}
                                  >
                                    Remove
                                  </button>
                                ) : (
                                  <span className="adm-owner-tag">Owner</span>
                                )}
                              </td>
                            </tr>
                          ))}
                          {wsMembers.length === 0 && (
                            <tr><td colSpan={5} className="adm-empty">No members found.</td></tr>
                          )}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ Approvals Tab ═══════════ */}
      {tab === 'approvals' && (
        <div className="adm-section">
          {pendingUsers.length === 0 ? (
            <div className="adm-empty-state">
              <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" width="48" height="48">
                <circle cx="10" cy="8" r="4" /><path d="M2 17c0-3.87 3.58-7 8-7s8 3.13 8 7" />
              </svg>
              <p>No pending approval requests.</p>
            </div>
          ) : (
            <>
              <p className="adm-pending-hint">
                The following users have registered and are awaiting approval before they can sign in.
              </p>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th>Email</th>
                      <th>Requested</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingUsers.map((u, i) => (
                      <tr key={u.id}>
                        <td>
                          <div className="adm-user-cell">
                            <div className="adm-avatar" style={{ background: DOT_COLORS[i % DOT_COLORS.length] }}>
                              {initials(u.name)}
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                              <span className="adm-name">{u.name}</span>
                              {u.username && <span className="adm-mono" style={{ fontSize: '0.78em', opacity: 0.6 }}>@{u.username}</span>}
                            </div>
                          </div>
                        </td>
                        <td className="adm-mono">{u.email}</td>
                        <td className="adm-date">{new Date(u.created_at).toLocaleDateString()}</td>
                        <td>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                              className="adm-action-btn adm-action-approve"
                              onClick={() => approveUser(u.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="adm-action-btn adm-action-reject"
                              onClick={() => rejectUser(u.id, u.name)}
                            >
                              Reject
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default AdminConsole;
